import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { useNavigate } from 'react-router-dom';
import { Mail, Briefcase } from 'lucide-react';

const Members = () => {
  const { token } = React.useContext(AuthContext);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    const filtered = members.filter(member => 
      member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (member.industry && member.industry.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (member.business_name && member.business_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredMembers(filtered);
  }, [searchTerm, members]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter out business accounts - only show individual members
      const individualMembers = response.data.filter(member => 
        !member.employee_id && // Not employees
        member.full_name && member.full_name.trim() !== '' // Has a name (not just business)
      );
      setMembers(individualMembers);
      setFilteredMembers(individualMembers);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="members-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Our Community Members
          </h1>
          <Input 
            placeholder="Search by name, industry, or business..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md glass"
            data-testid="members-search-input"
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredMembers.map((member) => (
            <Card key={member.id} className="glass hover:shadow-xl transition-all" data-testid={`member-card-${member.id}`}>
              <CardHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {member.full_name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>{member.full_name}</CardTitle>
                    {member.membership_tier === 'premium' && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Premium</span>
                    )}
                  </div>
                </div>
                {member.bio && <p className="text-sm text-gray-600 mb-2">{member.bio}</p>}
                {member.businesses && member.businesses.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 mb-1">Businesses:</div>
                    <div className="flex flex-wrap gap-2">
                      {member.businesses.map(business => (
                        <button
                          key={business.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/businesses?highlight=${business.id}`);
                          }}
                          className="flex items-center gap-1 text-xs bg-rose-50 text-rose-700 px-2 py-1 rounded-full hover:bg-rose-100 transition"
                        >
                          <Briefcase className="w-3 h-3" />
                          {business.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {member.business_name && !member.businesses?.length && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <Briefcase className="w-4 h-4" />
                    <span>{member.business_name}</span>
                  </div>
                )}
                {member.industry && (
                  <div className="text-xs text-rose-600 mt-2">{member.industry}</div>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => navigate(`/profile/${member.id}`)} 
                    className="flex-1 bg-rose-500 hover:bg-rose-600"
                    size="sm"
                    data-testid={`view-profile-${member.id}`}
                  >
                    View Profile
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex items-center gap-1"
                    data-testid={`message-${member.id}`}
                  >
                    <Mail className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredMembers.length === 0 && (
          <div className="text-center py-12" data-testid="no-members">
            <p className="text-gray-500">No members found</p>
          </div>
        )}

        {/* Upgrade prompt for basic users */}
        {React.useContext(AuthContext).user?.membership_tier === 'basic' && filteredMembers.length >= 10 && (
          <Card className="glass mt-8 max-w-2xl mx-auto" data-testid="upgrade-prompt">
            <CardContent className="text-center py-8">
              <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Want to See More Members?
              </h3>
              <p className="text-gray-700 mb-4">Upgrade to Premium or Corporate to access the full member directory</p>
              <Button onClick={() => navigate('/membership')} className="bg-rose-500 hover:bg-rose-600">
                View Membership Plans
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Members;